import { Link } from 'react-router-dom';
import { differenceInCalendarDays } from 'date-fns';
import { EyeOff } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { SectionCard } from '@/components/common/SectionCard';
import { LinkStatusCard } from '@/components/common/LinkStatusCard';
import { RenewalOfferPanel } from '@/components/common/RenewalOfferPanel';
import { usePermission } from '@/hooks/usePermission';
import { useRenewalOffer } from '@/hooks/useRenewalOffer';
import { useStartRenewal } from '@/hooks/useStartRenewal';
import { PERMISSIONS } from '@/lib/constants/permissions';
import { ROUTES } from '@/lib/constants/routes';
import type { ActiveMembership } from '@/lib/types/auth.types';
import { formatDate } from '@/lib/utils/date.utils';
import { isViewAsTab } from '@/lib/auth/session';
import type { DataRoomEntitlement } from '../api/data-room.api';
import { DATA_ROOM_STRINGS, DATA_ROOM_VOICE, ENDING_SOON_DAYS } from '../constants';
import { dataRoomHandoffHref } from '../data-room-links';
import { useDataRoomEntitlement } from '../hooks/useDataRoomEntitlement';
import { DataRoomNoSeat } from './DataRoomNoSeat';
import { DataRoomRefusal } from './DataRoomRefusal';
import { DataRoomSeatsCard } from './DataRoomSeatsCard';
import { IssueReportExplainer } from './IssueReportExplainer';

const COPY = DATA_ROOM_STRINGS.PAGE;

/**
 * The Legal Command Center's top for a startup member (MYN-331, FR-299),
 * first match wins (A-330):
 *
 * 0. A view-as tab: never the room (§51), with a notice saying so.
 * 1. Access open: the room in its service's voice with Open, or the existing
 *    per-member state (no seat, company email, suspended).
 * 2. A Startup Admin inside the renewal window: the renewal offer. While the
 *    package is still running it sits under the room instead, so it can be
 *    bought early (FR-297).
 * 3. Otherwise: the Issue Report explainer and its buy prompt.
 */
export function CommandCenterTop({ startup }: { startup: ActiveMembership }) {
  const startupId = startup.entityId;
  const entitlement = useDataRoomEntitlement(startupId);
  const canBuy = usePermission(PERMISSIONS.BILLING_MANAGE);
  const renewalOffer = useRenewalOffer(startupId, canBuy);
  const subscribe = useStartRenewal(startupId);
  const renewal = (
    <RenewalOfferPanel
      offer={renewalOffer.data}
      onSubscribe={(planId) => subscribe.mutate(planId)}
      pendingPlanId={subscribe.isPending ? (subscribe.variables ?? null) : null}
    />
  );

  // The Backend refuses the entitlement in a view-as session; say why, rather
  // than the outage state that refusal would otherwise fall into.
  if (isViewAsTab()) {
    return (
      <LinkStatusCard
        icon={<EyeOff size={32} />}
        heading={DATA_ROOM_STRINGS.VIEW_AS.TITLE}
        body={DATA_ROOM_STRINGS.VIEW_AS.BODY}
        ctaLabel={DATA_ROOM_STRINGS.BACK_TO_DASHBOARD}
        ctaTo={ROUTES.DASHBOARD}
        ctaVariant="link"
      />
    );
  }
  if (entitlement.isLoading) return <LoadingSpinner label={COPY.LOADING} />;
  if (!entitlement.data) {
    return (
      <DataRoomRefusal
        reason="upstream_unavailable"
        onRetry={() => void entitlement.refetch()}
        fullScreen={false}
      />
    );
  }
  if (entitlement.data.entitled) {
    return (
      <>
        <AccessPanel entitlement={entitlement.data} startupId={startupId} />
        {renewal}
        {canBuy && <DataRoomSeatsCard startupId={startupId} />}
      </>
    );
  }
  const { reason } = entitlement.data;
  if (reason === 'no_active_service') {
    return (
      <>
        {renewal}
        <IssueReportExplainer startupId={startupId} />
      </>
    );
  }
  if (reason === 'seat_not_assigned') return <DataRoomNoSeat membership={startup} />;
  return <DataRoomRefusal reason={reason} fullScreen={false} />;
}

/** The open room: its service's voice, its end date, and Open in a new tab. */
function AccessPanel({
  entitlement,
  startupId,
}: {
  entitlement: Extract<DataRoomEntitlement, { entitled: true }>;
  startupId: string;
}) {
  const voice = COPY[DATA_ROOM_VOICE[entitlement.service]];
  // Inside the renewal window the badge says how long is left (A-326).
  const daysLeft = differenceInCalendarDays(new Date(entitlement.endsAt), new Date());
  // Premium only: a Subscription's end date is its paid-through date, which
  // moves on at every renewal, so it is never "ending" in this sense.
  const endingSoon = entitlement.service === 'premium' && daysLeft <= ENDING_SOON_DAYS;
  return (
    <SectionCard>
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div className="flex max-w-xl flex-col gap-1">
          <p className="text-green text-xs font-semibold tracking-wide uppercase">
            {voice.eyebrow}
          </p>
          <h2 className="text-h3 text-navy">{voice.title}</h2>
          <p className="text-text-secondary text-sm">{voice.body}</p>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <Badge variant={endingSoon ? 'warning' : 'success'}>
              {endingSoon ? COPY.ENDS_IN(daysLeft) : COPY.STATUS_ACTIVE}
            </Badge>
            <span className="text-text-muted text-sm">
              {COPY.ENDS.replace('{date}', formatDate(entitlement.endsAt))}
            </span>
          </div>
        </div>
        {/* Opens in a new tab so My NEXT Raise stays open behind the data
            room, and closing that tab is the way back (UX round 3,
            StartupAdminPatty Y27/Y28). Router state does not cross tabs, so
            the startup goes in the query string, which the interstitial
            already reads for its return path (MYN-164). The launch call
            behind it is still entitlement-checked by the backend. */}
        <Button asChild variant="primary">
          <Link to={dataRoomHandoffHref(startupId)} target="_blank" rel="noopener noreferrer">
            {DATA_ROOM_STRINGS.LAUNCH.OPEN}
          </Link>
        </Button>
      </div>
    </SectionCard>
  );
}
